import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { HoverHelp } from '@/tableControls'
import { toast } from '@/store/toastStore'
import { getSettings, updateSetting } from '@/services/settings'
import type { LaborRoleMappingEntry, TipRoleGroupEntry } from '@/types/payrollPaymaster'
import {
  canAssignTipGroup,
  clearStationOnRoleRows,
  incompleteLaborRoleNames,
  isOrphanStation,
  isOrphanTipGroup,
  LABOR_GROUP_OPTIONS,
  mergeRoleRows,
  mergeStationRows,
  parseShareInput,
  renameStationOnRoleRows,
  renameTipGroupOnRoleRows,
  splitMergedRoleRows,
  splitStationRows,
  stationNamesFromRows,
  tipGroupNamesFromShareRows,
  validateNonzeroShareSum,
  validateStationNames,
  type MergedRoleRow,
  type StationRow,
} from './paymasterSettings'

const cardClass =
  'rounded-xl border border-gray-200 bg-white p-4 shadow-sm shadow-gray-950/[0.03]'

const ROLE_SAVE_DEBOUNCE_MS = 800

type ColumnDef<Row extends Record<string, string>> = {
  key: keyof Row & string
  header: string
  mono?: boolean
}

function rowsEqual<T>(a: T[], b: T[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

function parseJsonSetting<T>(raw: string | undefined, fallback: T, label: string): T {
  if (!raw?.trim()) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    toast.warning(`Could not parse ${label}; showing an empty table.`)
    return fallback
  }
}

function Toggle({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  ariaLabel: string
}) {
  return (
    <label className="toggle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={ariaLabel}
      />
      <span className="toggle-slider" />
    </label>
  )
}

function SettingsTableCard({
  title,
  description,
  loading,
  dirty,
  saving,
  onSave,
  children,
}: {
  title: string
  description: string
  loading: boolean
  dirty: boolean
  saving: boolean
  onSave: () => void
  children: ReactNode
}) {
  return (
    <div className={cardClass}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="container-title">{title}</h3>
          <p className="mt-1 text-xs leading-relaxed text-gray-500">{description}</p>
        </div>
        {dirty ? <span className="shrink-0 text-xs text-amber-600">Unsaved</span> : null}
      </div>
      {loading ? (
        <p className="mt-3 text-xs text-gray-500">Loading…</p>
      ) : (
        <div className="mt-4 space-y-3">
          {children}
          <Button gradient onClick={onSave} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  )
}

function MappingTable<Row extends Record<string, string>>({
  columns,
  rows,
  onChange,
  onRemove,
  onAdd,
}: {
  columns: ColumnDef<Row>[]
  rows: Row[]
  onChange: (index: number, key: keyof Row & string, value: string) => void
  onRemove: (index: number) => void
  onAdd: () => void
}) {
  return (
    <div className="space-y-2">
      <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
        <table className="table-forecast min-w-full">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.header}</th>
              ))}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="py-4 text-center text-xs text-gray-500"
                >
                  No rows yet — add one below.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={index} className="[&>td]:align-middle">
                  {columns.map((col) => (
                    <td key={col.key}>
                      <input
                        className={`input w-full min-w-[8rem] text-sm ${
                          col.mono ? 'font-mono' : ''
                        }`}
                        value={row[col.key]}
                        onChange={(e) => onChange(index, col.key, e.target.value)}
                      />
                    </td>
                  ))}
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost p-1 text-gray-500 hover:text-red-600"
                      onClick={() => onRemove(index)}
                      aria-label="Remove row"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <button type="button" className="btn btn-secondary text-sm" onClick={onAdd}>
        <PlusIcon className="h-4 w-4" />
        Add row
      </button>
    </div>
  )
}

function StationsTable({
  rows,
  onChange,
  onRemove,
  onAdd,
}: {
  rows: StationRow[]
  onChange: (index: number, value: string) => void
  onRemove: (index: number) => void
  onAdd: () => void
}) {
  return (
    <div className="space-y-2">
      <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
        <table className="table-forecast min-w-full">
          <thead>
            <tr>
              <th>Station</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2} className="py-4 text-center text-xs text-gray-500">
                  Sales stations appear after you run query in Labor Analytics. Add extra
                  stations below.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={row.locked ? `locked:${row.station}` : `custom:${index}`}
                  className="[&>td]:align-middle"
                >
                  <td>
                    <input
                      className="input w-full min-w-[8rem] text-sm"
                      value={row.station}
                      disabled={row.locked}
                      readOnly={row.locked}
                      onChange={(e) => onChange(index, e.target.value)}
                      aria-label={
                        row.locked ? `${row.station} (from sales data)` : 'Station'
                      }
                    />
                  </td>
                  <td>
                    {row.locked ? null : (
                      <button
                        type="button"
                        className="btn btn-ghost p-1 text-gray-500 hover:text-red-600"
                        onClick={() => onRemove(index)}
                        aria-label="Remove row"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <button type="button" className="btn btn-secondary text-sm" onClick={onAdd}>
        <PlusIcon className="h-4 w-4" />
        Add row
      </button>
    </div>
  )
}

type ShareRow = { group: string; share: string }
type MerchantRow = { merchantId: string; store: string }

function shareRowsFromRecord(record: Record<string, number>): ShareRow[] {
  return Object.entries(record).map(([group, share]) => ({
    group,
    share: String(share),
  }))
}

function shareRecordFromRows(rows: ShareRow[]): Record<string, number> | null {
  const record: Record<string, number> = {}
  for (const row of rows) {
    const group = row.group.trim()
    if (!group) continue
    const share = parseShareInput(row.share)
    if (share === null) return null
    record[group] = share
  }
  return record
}

function merchantRowsFromRecord(record: Record<string, string>): MerchantRow[] {
  return Object.entries(record).map(([merchantId, store]) => ({ merchantId, store }))
}

function merchantRecordFromRows(rows: MerchantRow[]): Record<string, string> {
  const record: Record<string, string> = {}
  for (const row of rows) {
    const merchantId = row.merchantId.trim()
    const store = row.store.trim()
    if (!merchantId || !store) continue
    record[merchantId] = store
  }
  return record
}

export function CloverMerchantsCard() {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<MerchantRow[]>([])
  const [savedRows, setSavedRows] = useState<MerchantRow[]>([])
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const data = await getSettings()
      const record = parseJsonSetting<Record<string, string>>(
        data.settings.clover_merchants?.value,
        {},
        'Clover merchants'
      )
      const nextRows = merchantRowsFromRecord(record)
      setRows(nextRows)
      setSavedRows(nextRows)
    } catch (err) {
      toast.error('Failed to load Clover merchants')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const dirty = useMemo(() => !rowsEqual(rows, savedRows), [rows, savedRows])

  async function handleSave() {
    const record = merchantRecordFromRows(rows)
    try {
      setSaving(true)
      await updateSetting('clover_merchants', { value: JSON.stringify(record) })
      const nextRows = merchantRowsFromRecord(record)
      setRows(nextRows)
      setSavedRows(nextRows)
      toast.success('Clover merchants saved')
    } catch (err) {
      toast.error('Failed to save Clover merchants')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsTableCard
      title="Clover merchants"
      description="Map Clover merchant IDs to store names. Paste API tokens in Settings → Clover integration."
      loading={loading}
      dirty={dirty}
      saving={saving}
      onSave={() => void handleSave()}
    >
      <MappingTable
        columns={[
          { key: 'merchantId', header: 'Merchant ID', mono: true },
          { key: 'store', header: 'Store' },
        ]}
        rows={rows}
        onChange={(index, key, value) =>
          setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)))
        }
        onRemove={(index) => setRows((prev) => prev.filter((_, i) => i !== index))}
        onAdd={() => setRows((prev) => [...prev, { merchantId: '', store: '' }])}
      />
    </SettingsTableCard>
  )
}

export function PaymasterTipAndRoleSettings() {
  const [loading, setLoading] = useState(true)
  const [shareRows, setShareRows] = useState<ShareRow[]>([])
  const [savedShareRows, setSavedShareRows] = useState<ShareRow[]>([])
  const [shareSaving, setShareSaving] = useState(false)
  const [shareValidationError, setShareValidationError] = useState<string | null>(null)
  const [stationRows, setStationRows] = useState<StationRow[]>([])
  const [savedStationRows, setSavedStationRows] = useState<StationRow[]>([])
  const [stationSaving, setStationSaving] = useState(false)
  const [roleRows, setRoleRows] = useState<MergedRoleRow[]>([])
  const [savedRoleRows, setSavedRoleRows] = useState<MergedRoleRow[]>([])
  const [roleSaving, setRoleSaving] = useState(false)
  const [roleSaveError, setRoleSaveError] = useState(false)
  const [roleDidSave, setRoleDidSave] = useState(false)
  const roleRowsRef = useRef(roleRows)
  const savedRoleRowsRef = useRef(savedRoleRows)
  const persistLockRef = useRef<Promise<void> | null>(null)
  roleRowsRef.current = roleRows
  savedRoleRowsRef.current = savedRoleRows

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const settings = await getSettings()
      const shareRecord = parseJsonSetting<Record<string, number>>(
        settings.settings.tip_group_shares?.value,
        {},
        'tip groups'
      )
      const tipRoles = parseJsonSetting<Record<string, TipRoleGroupEntry>>(
        settings.settings.tip_role_groups?.value,
        {},
        'tip role groups'
      )
      const laborRoles = parseJsonSetting<Record<string, LaborRoleMappingEntry>>(
        settings.settings.labor_role_mapping?.value,
        {},
        'labor role mapping'
      )
      const stations = parseJsonSetting<{ included: string[] }>(
        settings.settings.labor_stations?.value,
        { included: [] },
        'labor stations'
      )
      const analyticsStations = parseJsonSetting<{ names?: string[]; locked?: string[] }>(
        settings.settings.labor_analytics_stations?.value,
        { names: [], locked: [] },
        'labor analytics stations'
      )
      const nextShareRows = shareRowsFromRecord(shareRecord)
      const nextStationRows = mergeStationRows(analyticsStations)
      const nextRoleRows = mergeRoleRows(tipRoles, laborRoles, stations.included)
      setShareRows(nextShareRows)
      setSavedShareRows(nextShareRows)
      setStationRows(nextStationRows)
      setSavedStationRows(nextStationRows)
      setRoleRows(nextRoleRows)
      setSavedRoleRows(nextRoleRows)
    } catch (err) {
      toast.error('Failed to load tip groups and roles')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const sharesDirty = useMemo(() => !rowsEqual(shareRows, savedShareRows), [shareRows, savedShareRows])
  const stationsDirty = useMemo(
    () => !rowsEqual(stationRows, savedStationRows),
    [stationRows, savedStationRows],
  )
  const rolesDirty = useMemo(() => !rowsEqual(roleRows, savedRoleRows), [roleRows, savedRoleRows])
  const incompleteRoles = useMemo(() => incompleteLaborRoleNames(roleRows), [roleRows])
  const tipGroupNames = useMemo(() => tipGroupNamesFromShareRows(shareRows), [shareRows])
  const stationNames = useMemo(
    () =>
      stationNamesFromRows(stationRows).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      ),
    [stationRows],
  )
  const orphanTipGroupRoles = useMemo(
    () =>
      roleRows
        .filter((row) => isOrphanTipGroup(row.tipGroup, tipGroupNames))
        .map((row) => row.role),
    [roleRows, tipGroupNames]
  )
  const orphanStationRoles = useMemo(
    () =>
      roleRows
        .filter((row) => isOrphanStation(row.station, stationNames))
        .map((row) => row.role),
    [roleRows, stationNames]
  )

  useEffect(() => {
    const record = shareRecordFromRows(shareRows)
    if (record === null) {
      setShareValidationError('Enter valid decimal or percent shares')
      return
    }
    setShareValidationError(validateNonzeroShareSum(record))
  }, [shareRows])

  function handleShareChange(index: number, key: keyof ShareRow & string, value: string) {
    if (key === 'group') {
      const from = shareRows[index]?.group ?? ''
      setRoleRows((prev) => renameTipGroupOnRoleRows(prev, from, value))
    }
    setShareRows((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)))
  }

  function handleStationChange(index: number, value: string) {
    const current = stationRows[index]
    if (!current || current.locked) return
    setRoleRows((prev) => renameStationOnRoleRows(prev, current.station, value))
    setStationRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, station: value } : row)),
    )
  }

  function handleRemoveStation(index: number) {
    const removed = stationRows[index]
    if (!removed || removed.locked) return
    setStationRows((prev) => prev.filter((_, i) => i !== index))
    setRoleRows((prev) => clearStationOnRoleRows(prev, removed.station))
  }

  async function handleSaveStations() {
    const error = validateStationNames(stationRows)
    if (error) {
      toast.warning(error)
      return
    }
    const payload = splitStationRows(stationRows)
    try {
      setStationSaving(true)
      await updateSetting('labor_analytics_stations', {
        value: JSON.stringify(payload),
      })
      const nextStationRows = mergeStationRows(payload)
      setStationRows(nextStationRows)
      setSavedStationRows(nextStationRows)
      toast.success('Stations saved')
      if (!rowsEqual(roleRowsRef.current, savedRoleRowsRef.current)) {
        try {
          await persistRoleRowsNow()
        } catch {
          /* Role autosave toasts its own failure. */
        }
      }
    } catch (err) {
      toast.error('Failed to save stations')
      console.error(err)
    } finally {
      setStationSaving(false)
    }
  }

  const persistRoleRowsNow = useCallback(() => {
    const next = (persistLockRef.current ?? Promise.resolve())
      .catch(() => undefined)
      .then(async () => {
        if (rowsEqual(roleRowsRef.current, savedRoleRowsRef.current)) return
        setRoleSaving(true)
        setRoleSaveError(false)
        try {
          while (true) {
            const snapshot = roleRowsRef.current
            if (rowsEqual(snapshot, savedRoleRowsRef.current)) break
            const { tipRoles, laborRoles, includedStations } = splitMergedRoleRows(snapshot)
            await Promise.all([
              updateSetting('tip_role_groups', { value: JSON.stringify(tipRoles) }),
              updateSetting('labor_role_mapping', { value: JSON.stringify(laborRoles) }),
              updateSetting('labor_stations', {
                value: JSON.stringify({ included: includedStations }),
              }),
            ])
            savedRoleRowsRef.current = snapshot
            setSavedRoleRows(snapshot)
            setRoleDidSave(true)
          }
        } catch (err) {
          setRoleSaveError(true)
          toast.error('Failed to save role mappings')
          console.error(err)
          throw err
        } finally {
          setRoleSaving(false)
        }
      })
    persistLockRef.current = next
    return next
  }, [])

  useEffect(() => {
    if (loading) return
    if (!rolesDirty) return
    const timer = window.setTimeout(() => {
      void persistRoleRowsNow()
    }, ROLE_SAVE_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [loading, persistRoleRowsNow, roleRows, rolesDirty])

  useEffect(() => {
    return () => {
      if (!rowsEqual(roleRowsRef.current, savedRoleRowsRef.current)) {
        void persistRoleRowsNow()
      }
    }
  }, [persistRoleRowsNow])

  async function handleSaveShares() {
    const record = shareRecordFromRows(shareRows)
    if (record === null) {
      toast.warning('Enter valid decimal or percent shares')
      return
    }
    const error = validateNonzeroShareSum(record)
    if (error) {
      toast.warning(error)
      return
    }
    try {
      setShareSaving(true)
      await updateSetting('tip_group_shares', { value: JSON.stringify(record) })
      const nextShareRows = shareRowsFromRecord(record)
      setShareRows(nextShareRows)
      setSavedShareRows(nextShareRows)
      toast.success('Tip groups saved')
      if (!rowsEqual(roleRowsRef.current, savedRoleRowsRef.current)) {
        try {
          await persistRoleRowsNow()
        } catch {
          /* Role autosave toasts its own failure. */
        }
      }
    } catch (err) {
      toast.error('Failed to save tip groups')
      console.error(err)
    } finally {
      setShareSaving(false)
    }
  }

  return (
    <>
      <SettingsTableCard
        title="Tip groups"
        description="Non-zero group shares must sum to 100% (1.0). Use decimals (0.5) or percents (50%)."
        loading={loading}
        dirty={sharesDirty}
        saving={shareSaving}
        onSave={() => void handleSaveShares()}
      >
        {shareValidationError ? <p className="text-xs text-amber-700">{shareValidationError}</p> : null}
        <MappingTable
          columns={[
            { key: 'group', header: 'Group' },
            { key: 'share', header: 'Share (0–1 or %)' },
          ]}
          rows={shareRows}
          onChange={handleShareChange}
          onRemove={(index) => setShareRows((prev) => prev.filter((_, i) => i !== index))}
          onAdd={() => setShareRows((prev) => [...prev, { group: '', share: '' }])}
        />
      </SettingsTableCard>

      <SettingsTableCard
        title="Stations"
        description="Stations from sales data cannot be edited. Add extra stations for Homebase roles that do not match a sales station. Labor Analytics refresh adds new sales stations to this list without removing existing ones."
        loading={loading}
        dirty={stationsDirty}
        saving={stationSaving}
        onSave={() => void handleSaveStations()}
      >
        <StationsTable
          rows={stationRows}
          onChange={handleStationChange}
          onRemove={handleRemoveStation}
          onAdd={() => setStationRows((prev) => [...prev, { station: '', locked: false }])}
        />
      </SettingsTableCard>

      <div className={cardClass}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="container-title">Roles</h3>
            <p className="mt-1 text-xs leading-relaxed text-gray-500">
              Map Homebase roles onto Stations for hours and wages. Also set tip groups, labor
              groups, eligibility, and whether a role is excluded from labor analytics
              denominators.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {roleSaveError ? (
              <button
                type="button"
                className="text-xs text-red-700 underline decoration-red-300 underline-offset-2 hover:text-red-800"
                onClick={() => void persistRoleRowsNow()}
              >
                Save failed — retry
              </button>
            ) : rolesDirty || roleSaving ? (
              <span className="text-xs text-gray-500">Saving…</span>
            ) : roleDidSave ? (
              <span className="text-xs text-emerald-700">Saved</span>
            ) : null}
          </div>
        </div>
        {incompleteRoles.length > 0 ? (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <strong>Roles need a labor group, location, and station (when not excluded):</strong>{' '}
            {incompleteRoles.join(', ')}
          </div>
        ) : null}
        {orphanTipGroupRoles.length > 0 ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
            <strong>Tip group was removed from Tip groups:</strong> {orphanTipGroupRoles.join(', ')}
          </div>
        ) : null}
        {orphanStationRoles.length > 0 ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
            <strong>Station was removed from Stations:</strong> {orphanStationRoles.join(', ')}
          </div>
        ) : null}
        {loading ? (
          <p className="mt-3 text-xs text-gray-500">Loading…</p>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
              <table className="table-forecast min-w-full">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Location</th>
                    <th>Labor group</th>
                    <th>Station</th>
                    <th>Tip group</th>
                    <th>
                      <HoverHelp label="Tip eligible" aria-label="Tip eligible help">
                        Eligible roles receive a share of the daily tip pool.
                      </HoverHelp>
                    </th>
                    <th>
                      <HoverHelp
                        label="Exclude from denominators"
                        aria-label="Exclude from denominators help"
                      >
                        Excluded roles are omitted from labor analytics hours and wage
                        denominators.
                      </HoverHelp>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roleRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-4 text-center text-xs text-gray-500">
                        Roles appear after you run Calculate tips or Run query in Labor Analytics.
                      </td>
                    </tr>
                  ) : (
                    roleRows.map((row, index) => {
                      const tipGroupAssignable = canAssignTipGroup(row.laborGroup)
                      const orphanTipGroup =
                        tipGroupAssignable && isOrphanTipGroup(row.tipGroup, tipGroupNames)
                      const orphanStation = isOrphanStation(row.station, stationNames)
                      return (
                        <tr key={row.role} className="[&>td]:align-middle">
                          <td className="whitespace-nowrap text-sm text-gray-900">{row.role}</td>
                          <td>
                            <select
                              className="input select w-full min-w-[6rem] text-sm"
                              value={row.locationType}
                              onChange={(e) =>
                                setRoleRows((prev) =>
                                  prev.map((item, i) =>
                                    i === index ? { ...item, locationType: e.target.value } : item
                                  )
                                )
                              }
                            >
                              <option value="">Select…</option>
                              <option value="Store">Store</option>
                              <option value="GA">GA</option>
                            </select>
                          </td>
                          <td>
                            <select
                              className="input select w-full min-w-[6rem] text-sm"
                              value={row.laborGroup}
                              onChange={(e) => {
                                const laborGroup = e.target.value
                                setRoleRows((prev) =>
                                  prev.map((item, i) =>
                                    i === index
                                      ? {
                                          ...item,
                                          laborGroup,
                                          ...(canAssignTipGroup(laborGroup) ? {} : { tipGroup: '' }),
                                        }
                                      : item
                                  )
                                )
                              }}
                            >
                              <option value="">Select…</option>
                              {LABOR_GROUP_OPTIONS.map((group) => (
                                <option key={group} value={group}>
                                  {group}
                                </option>
                              ))}
                              {row.laborGroup &&
                              !(LABOR_GROUP_OPTIONS as readonly string[]).includes(row.laborGroup) ? (
                                <option value={row.laborGroup}>{row.laborGroup}</option>
                              ) : null}
                            </select>
                          </td>
                          <td>
                            <select
                              className={`input select w-full min-w-[6rem] text-sm ${
                                orphanStation ? 'border-red-400 text-red-800' : ''
                              }`}
                              value={row.station}
                              onChange={(e) =>
                                setRoleRows((prev) =>
                                  prev.map((item, i) =>
                                    i === index ? { ...item, station: e.target.value } : item
                                  )
                                )
                              }
                            >
                              <option value="">Select…</option>
                              {stationNames.map((station) => (
                                <option key={station} value={station}>
                                  {station}
                                </option>
                              ))}
                              {orphanStation ? (
                                <option value={row.station}>{row.station} (removed)</option>
                              ) : null}
                            </select>
                            {orphanStation ? (
                              <p className="mt-1 text-[11px] text-red-700">
                                This station was removed from Stations.
                              </p>
                            ) : null}
                          </td>
                          <td>
                            <select
                              className={`input select w-full min-w-[6rem] text-sm ${
                                orphanTipGroup ? 'border-red-400 text-red-800' : ''
                              }`}
                              value={tipGroupAssignable ? row.tipGroup : ''}
                              disabled={!tipGroupAssignable}
                              aria-invalid={orphanTipGroup}
                              onChange={(e) =>
                                setRoleRows((prev) =>
                                  prev.map((item, i) =>
                                    i === index ? { ...item, tipGroup: e.target.value } : item
                                  )
                                )
                              }
                            >
                              <option value="">Select…</option>
                              {tipGroupNames.map((group) => (
                                <option key={group} value={group}>
                                  {group}
                                </option>
                              ))}
                              {orphanTipGroup ? (
                                <option value={row.tipGroup}>{row.tipGroup} (removed)</option>
                              ) : null}
                            </select>
                            {orphanTipGroup ? (
                              <p className="mt-1 text-[11px] text-red-700">
                                This tip group was removed from Tip groups.
                              </p>
                            ) : null}
                          </td>
                          <td>
                            <div className="flex items-center">
                              <Toggle
                                checked={row.eligible}
                                onChange={(checked) =>
                                  setRoleRows((prev) =>
                                    prev.map((item, i) =>
                                      i === index ? { ...item, eligible: checked } : item
                                    )
                                  )
                                }
                                ariaLabel={`Tip eligible for ${row.role || 'role'}`}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center">
                              <Toggle
                                checked={row.excludeFromDenominators}
                                onChange={(checked) =>
                                  setRoleRows((prev) =>
                                    prev.map((item, i) =>
                                      i === index
                                        ? { ...item, excludeFromDenominators: checked }
                                        : item
                                    )
                                  )
                                }
                                ariaLabel={`Exclude ${row.role || 'role'} from denominators`}
                              />
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
